<template>
  <span
    class="info-tooltip-trigger"
    @mouseenter="show"
    @mouseleave="hide"
    @mousemove="setPosition"
  >
    <slot />
    <FontAwesomeIcon
      v-if="showIcon"
      icon="circle-question"
      class="ms-1 text-muted small"
    />
    <Teleport to="body">
      <div v-if="visible" class="info-tooltip-popup" :style="popupStyle">
        {{ text }}
      </div>
    </Teleport>
  </span>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

withDefaults(
  defineProps<{
    text: string;
    showIcon?: boolean;
  }>(),
  {
    showIcon: true,
  }
);

const CURSOR_GAP_PX = 12;
const VIEWPORT_MARGIN_PX = 8;
const CURSOR_RISE_PX = 32;

const visible = ref(false);
const cursorX = ref(0);
const cursorY = ref(0);

/**
 * Anchors the popup to the viewport edge nearest the cursor so it expands toward the far side.
 * Anchoring `left` unconditionally leaves the popup only the space between the cursor and the
 * right edge, which the browser shrinks to a one-word-wide column when the trigger sits near
 * the right of the screen.
 */
const popupStyle = computed(() => ({
  ...(cursorX.value > window.innerWidth / 2
    ? { right: `${window.innerWidth - cursorX.value + CURSOR_GAP_PX}px` }
    : { left: `${cursorX.value + CURSOR_GAP_PX}px` }),
  ...(cursorY.value > window.innerHeight / 2
    ? { bottom: `${window.innerHeight - cursorY.value + CURSOR_GAP_PX}px` }
    : {
        top: `${Math.max(cursorY.value - CURSOR_RISE_PX, VIEWPORT_MARGIN_PX)}px`,
      }),
}));

const show = (event: MouseEvent) => {
  setPosition(event);
  visible.value = true;
};

const hide = () => {
  visible.value = false;
};

const setPosition = (event: MouseEvent) => {
  cursorX.value = event.clientX;
  cursorY.value = event.clientY;
};
</script>

<style scoped lang="scss">
.info-tooltip-trigger {
  cursor: help;
}

:global(.info-tooltip-popup) {
  position: fixed;
  z-index: 9999;
  max-width: 280px;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: normal;
  line-height: 1.4;
  color: #fff;
  background-color: #333;
  border-radius: 0.375rem;
  white-space: normal;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: none;
}
</style>
